import { useTranslation } from 'react-i18next';
import { Alert, Button, Checkbox, FormControlLabel, Grid, Snackbar, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import { a11yLight } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { ContentCopy } from '@mui/icons-material';
import { AccountData, OrderPageData } from '../../../api';
import { SettingsRow } from '../../formComponents/SettingsRow';

const SyntaxHighlighterWithFix = SyntaxHighlighter as unknown as React.FC<any>;

type IntegrationSectionProps = {
  orderPage: OrderPageData;
  account: AccountData;
};
export const IntegrationSection = ({ orderPage, account }: IntegrationSectionProps) => {
  const [copySnackIsOpen, setCopySnackIsOpen] = useState(false);
  const [includeVouchers, setIncludeVouchers] = useState(false);
  const [includeCouples, setIncludeCouples] = useState(false);

  const { t } = useTranslation();
  const theme = useTheme();

  const posthog = usePostHog();
  const buildQueryString = () => {
    const params = new URLSearchParams();
    if (includeVouchers) params.append('vouchers', 'true');
    if (includeCouples) params.append('couples', 'true');
    return params.toString();
  };
  const code =
    '<div class="monkeybook-widget-container"\n' +
    `     data-iframe-src="https://app.monkeybook.io/order/${account.id}/${account.experiences[0].id}/${
      orderPage.id
    }?${buildQueryString()}"></div>\n\n` +
    `<script src="https://widget.monkeybook.io/widget.js?account=${account.id}" defer></script>`;

  const handleClick = () => {
    setCopySnackIsOpen(true);
    void navigator.clipboard.writeText(code);
    posthog.capture('Code snippet copied', {
      accountId: account.id,
      orderPageId: account.id,
    });
  };

  return (
    <Grid container flexDirection="column">
      <SettingsRow label={t('integrationSection.specialWidgets')}>
        <Grid container flexDirection="column">
          <FormControlLabel
            control={<Checkbox checked={includeVouchers} onChange={(e) => setIncludeVouchers(e.target.checked)} />}
            label={t('integrationSection.includeVouchers')}
          />
          <FormControlLabel
            control={<Checkbox checked={includeCouples} onChange={(e) => setIncludeCouples(e.target.checked)} />}
            label={t('integrationSection.includeCouples')}
          />
        </Grid>
      </SettingsRow>
      <SettingsRow label={t('tabs.integration')} isLast>
        <Grid container flexDirection="column" alignItems="flex-start" gap={2}>
          <Button variant="outlined" color="inherit" startIcon={<ContentCopy />} onClick={handleClick}>
            {t('integrationSection.copyCode')}
          </Button>
          <Grid container dir={'ltr'}>
            <SyntaxHighlighterWithFix
              wrapLines
              wrapLongLines
              language={'java'}
              style={a11yLight}
              customStyle={{
                padding: 16,
                margin: 0,
                width: '100%',
                fontSize: '14px',
                lineHeight: '20px',
                borderRadius: '8px',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
              }}>
              {code}
            </SyntaxHighlighterWithFix>
          </Grid>
        </Grid>
      </SettingsRow>
      <Snackbar
        open={copySnackIsOpen}
        onClose={() => setCopySnackIsOpen(false)}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setCopySnackIsOpen(false)} severity="success">
          {t('copiedToClipboard')}
        </Alert>
      </Snackbar>
    </Grid>
  );
};
